import { useCallback, useEffect, useRef, useState } from 'react';

// The check-your-email screen's two buttons, and what each one answers (#566).
//
// The screen is src/components/auth/CheckEmailScreen.jsx. This hook holds the
// decisions, kept out of the component so a test can drive it with a fake
// `resend`, `refresh` and `say`. Its answers go to the toast (#568).
//
// `resend()` asks Firebase to send the link again. `refresh()` reloads the
// Firebase user and resolves with whether it is now verified. If it is, the
// gate in App.jsx opens and the screen unmounts, so a verified check says
// nothing: the app appearing is the answer. `say(text)` is the toast.

/** What the buttons answer. The address is named so a typo in it can be seen. */
export const CHECK_EMAIL_SAYS = Object.freeze({
  sent: (email) => `Sent another link to ${email}.`,
  // Firebase rate-limits sending, which is the usual reason this fails, and
  // the sentence says what to do about it.
  sendFailed: 'Could not send just now. Try again in a minute.',
  notYet: 'Not confirmed yet. Open the link in the email first.',
  checkFailed: 'Could not check just now. Check your connection and try again.',
});

/**
 * `busy` is null, 'sending' or 'checking'.
 *
 * A press while either is in flight is ignored rather than queued. Two sends
 * a second apart are what trips Firebase's rate limit, and a check started
 * during a send would report on an address the teacher has not had a chance
 * to confirm yet.
 */
export function useCheckEmail({ email, resend, refresh, say }) {
  const [busy, setBusy] = useState(null);
  // Read synchronously, so a second press in the same tick, before React has
  // re-rendered with the first, still sees the first.
  const inFlight = useRef(null);
  // A press can finish after the screen has gone: the usual case is "I've
  // verified" succeeding, which opens the gate and unmounts this.
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);

  const begin = useCallback((what) => {
    if (inFlight.current) return false;
    inFlight.current = what;
    setBusy(what);
    return true;
  }, []);
  const end = useCallback(() => {
    inFlight.current = null;
    if (mounted.current) setBusy(null);
  }, []);

  const onResend = useCallback(async () => {
    if (!begin('sending')) return;
    try {
      await resend();
      say(CHECK_EMAIL_SAYS.sent(email));
    } catch {
      say(CHECK_EMAIL_SAYS.sendFailed);
    }
    end();
  }, [begin, end, email, resend, say]);

  const onCheck = useCallback(async () => {
    if (!begin('checking')) return;
    let verified = false;
    try {
      verified = await refresh();
    } catch {
      say(CHECK_EMAIL_SAYS.checkFailed);
      end();
      return;
    }
    if (!verified) say(CHECK_EMAIL_SAYS.notYet);
    end();
  }, [begin, end, refresh, say]);

  return {
    busy,
    sending: busy === 'sending',
    checking: busy === 'checking',
    resend: onResend,
    check: onCheck,
  };
}
